/**
 * Dragging a scheduled task along its robot's row (Day view; Akash,
 * 2026-10-30, "both": drag for a nudge, the panel's Move-to field for an
 * exact time and the keyboard). Pure: the arithmetic and the words of the
 * bubble that follows the pointer.
 *
 *  - Only a SCHEDULED occurrence moves (outlined, not done, running or
 *    queued), and only for a viewer the screen gave the move callback to.
 *  - Along the row only: a drag never moves a task to another robot.
 *  - Snaps to 5 minutes, and starts only past a few pixels, so a click
 *    still selects.
 *  - The chip stays under the pointer: its time is read from where the
 *    pointer is over the track at the current scroll, so the lanes may
 *    scroll under a drag (by the wheel, or by the drag itself near an
 *    edge) and the time stays true. It lands only in the hours drawn.
 *  - A drag whose row scrolls wholly out of view is cancelled, as Esc.
 *  - Dropping goes through the same move path as the field -- the screen
 *    asks "this time only, or the schedule from now on?".
 */
import { formatZonedClock } from '../zoned-time';
import { paintOf, type LaneItem } from './lane-items';
import { LANE_CODE } from './lane-style';
import type { TimeWindow } from './lane-time';

export const DRAG_SNAP_MS = 5 * 60_000;
/** How far the pointer goes before a press becomes a drag, px. */
export const DRAG_THRESHOLD_PX = 4;

/** Whether `item` may be dragged at all (the viewer's right to is the
 *  caller's to check). */
export function isDraggable(item: Pick<LaneItem, 'kind' | 'outcome' | 'occurrence'>): boolean {
  return item.kind === 'occurrence' && item.outcome === 'scheduled' && Boolean(item.occurrence);
}

/**
 * Where the lanes are on screen at this moment: enough to turn a
 * pointer's x into a time over the track AT THE CURRENT SCROLL, so the
 * chip stays under the pointer when the lanes scroll beneath it.
 */
export interface DragGeometry {
  /** The scroller's left content edge, client px. */
  scrollerLeft: number;
  scrollLeft: number;
  /** The sticky robot-name column, px. */
  nameW: number;
  /** The time at the track's x = 0. */
  axisFromMs: number;
  pxPerMs: number;
}

/** The time under client x `clientX`. */
export function timeAtClientX(clientX: number, g: DragGeometry): number {
  return g.axisFromMs + (clientX - g.scrollerLeft - g.nameW + g.scrollLeft) / g.pxPerMs;
}

/**
 * The hours a drag may land in: the hours drawn (07:00-19:00, or the
 * whole day with "24 h"), widened only as far as the task's own start
 * when that is outside them -- so a task can be dragged back into the
 * hours, never further out.
 */
export function dragBounds(hours: TimeWindow, itemStartMs: number): TimeWindow {
  return {
    fromMs: Math.min(hours.fromMs, itemStartMs),
    toMs: Math.max(hours.toMs, itemStartMs + DRAG_SNAP_MS),
  };
}

/**
 * The start a drag gives a task: the time under the pointer, less where
 * on the chip it was grabbed (`grabOffsetMs`), snapped to 5 minutes on
 * the clock and kept inside `bounds`.
 */
export function dragTarget(
  clientX: number,
  grabOffsetMs: number,
  g: DragGeometry,
  bounds: TimeWindow,
  snapMs = DRAG_SNAP_MS,
): number {
  if (!(g.pxPerMs > 0)) return bounds.fromMs;
  const raw = timeAtClientX(clientX, g) - grabOffsetMs;
  const snapped = g.axisFromMs + Math.round((raw - g.axisFromMs) / snapMs) * snapMs;
  return Math.min(Math.max(snapped, bounds.fromMs), bounds.toMs - snapMs);
}

/** How close to the lanes' left or right edge a drag scrolls them, px. */
export const EDGE_SCROLL_ZONE_PX = 32;
/** How far they scroll per frame, px (~360 px a second). */
export const EDGE_SCROLL_STEP_PX = 6;

/**
 * How far to scroll the lanes this frame while dragging (negative =
 * left), or 0: only with the pointer near an edge, only while there is
 * more to scroll that way, and only while the hours a drop may land in
 * reach past what is on screen.
 */
export function edgeScrollStep(args: {
  clientX: number;
  g: DragGeometry;
  /** The scroller's visible width and how far it can scroll. */
  clientWidth: number;
  maxScrollLeft: number;
  bounds: TimeWindow;
  zonePx?: number;
  stepPx?: number;
}): number {
  const { clientX, g, clientWidth, maxScrollLeft, bounds } = args;
  const zone = args.zonePx ?? EDGE_SCROLL_ZONE_PX;
  const step = args.stepPx ?? EDGE_SCROLL_STEP_PX;
  if (!(g.pxPerMs > 0) || clientWidth <= g.nameW) return 0;
  const trackLeft = g.scrollerLeft + g.nameW;
  const trackRight = g.scrollerLeft + clientWidth;
  const shownFrom = g.axisFromMs + g.scrollLeft / g.pxPerMs;
  const shownTo = g.axisFromMs + (g.scrollLeft + clientWidth - g.nameW) / g.pxPerMs;
  if (clientX < trackLeft + zone && g.scrollLeft > 0 && shownFrom > bounds.fromMs) {
    return -Math.min(step, g.scrollLeft);
  }
  if (clientX > trackRight - zone && g.scrollLeft < maxScrollLeft && shownTo < bounds.toMs) {
    return Math.min(step, maxScrollLeft - g.scrollLeft);
  }
  return 0;
}

/**
 * Whether a row has scrolled wholly out of the lanes' view (`viewTop`
 * is below the sticky hour labels). A row not laid out (no height) is
 * never "out".
 */
export function isRowOutOfView(
  row: { top: number; bottom: number; height: number },
  viewTop: number,
  viewBottom: number,
): boolean {
  if (row.height <= 0) return false;
  return row.bottom <= viewTop || row.top >= viewBottom;
}

/** "15 min later", "1 h 05 min earlier", "same time". */
export function describeShift(deltaMs: number): string {
  const min = Math.round(Math.abs(deltaMs) / 60_000);
  if (min === 0) return 'same time';
  const size =
    min < 60 ? `${min} min` : `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')} min`;
  return `${size} ${deltaMs > 0 ? 'later' : 'earlier'}`;
}

/**
 * The first other task on the same row that the moved task would
 * overlap, in time order, or null. The moved task keeps its drawn length
 * (30 minutes for an occurrence); a zero-length neighbour counts at its
 * instant.
 */
export function findClash(
  item: Pick<LaneItem, 'id' | 'startMs' | 'endMs'>,
  newStartMs: number,
  rowItems: readonly LaneItem[],
): LaneItem | null {
  const newEnd = newStartMs + Math.max(item.endMs - item.startMs, 1);
  let best: LaneItem | null = null;
  for (const other of rowItems) {
    if (other.id === item.id) continue;
    const end = Math.max(other.endMs, other.startMs + 1);
    if (other.startMs < newEnd && end > newStartMs) {
      if (!best || other.startMs < best.startMs) best = other;
    }
  }
  return best;
}

export interface DragBubble {
  /** "14:20 · 15 min later". */
  when: string;
  /** "clashes with Sw 14:25", or null. */
  clash: string | null;
}

export function dragBubble(
  item: LaneItem,
  newStartMs: number,
  rowItems: readonly LaneItem[],
  timeZone: string,
): DragBubble {
  const other = findClash(item, newStartMs, rowItems);
  return {
    when: `${formatZonedClock(newStartMs, timeZone)} · ${describeShift(newStartMs - item.startMs)}`,
    clash: other
      ? `clashes with ${LANE_CODE[paintOf(other)]} ${formatZonedClock(other.startMs, timeZone)}`
      : null,
  };
}
